/**
 * Kickard PHASE 2.2.3 — Online WebSocket client with mid-match reconnect.
 *
 * Reconnect strategy
 * ------------------
 *   • Closes BEFORE `joined` is received → terminal error (no retry).
 *   • Server-issued NON_RECOVERABLE codes (4000/4401/4404/4500/4503) →
 *     terminal error.
 *   • Any other close AFTER joining (1006 network drop, 1001 going-away,
 *     proxy idle-kill, etc.) → auto-reconnect with exponential backoff,
 *     reusing the SAME room code + token. Reconnect budget = 30 seconds.
 *   • Heartbeat: every HEARTBEAT_MS the client sends `{type:'ping'}` so
 *     the proxy does not idle-kill the WS during long opponent turns.
 *
 * Callbacks (all optional)
 * ------------------------
 *   onJoined(msg)                — first join or reattach
 *   onState(state)               — legacy partial; prefer onStateMessage
 *   onStateMessage(msg)          — full {type,tick,stateHash,state}
 *   onEvents(events, tick)
 *   onError(message)             — UI-surfaceable (terminal) error
 *   onClose()                    — socket closed and won't retry
 *   onOpponentDisconnected()
 *   onReconnecting({ attempt, elapsedMs, totalMs })
 *   onReconnected()
 */

const HTTP_BASE = process.env.REACT_APP_BACKEND_URL;
if (!HTTP_BASE) {
  // eslint-disable-next-line no-console
  console.warn('REACT_APP_BACKEND_URL is not set — online mode will fail to connect');
}
const WS_BASE = (HTTP_BASE || '').replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');

const RECONNECT_BUDGET_MS = 30_000;
const RECONNECT_BASE_DELAY_MS = 500;
const RECONNECT_MAX_DELAY_MS = 3_000;
const HEARTBEAT_MS = 15_000;
const HEARTBEAT_DEAD_MS = 25_000; // if no msg from server in this long → force close+reconnect

// Server-issued close codes that mean "do NOT retry, this is terminal".
const NON_RECOVERABLE_CODES = new Set([4000, 4401, 4404, 4500, 4503]);

const CODE_MSGS = {
  1006: 'Connection closed unexpectedly. Check network / firewall.',
  4000: 'Connection replaced by a newer one (refresh or duplicate tab).',
  4401: 'Authentication failed (bad token). Please leave and create a new room.',
  4404: 'Room not found or expired. Please create a new room.',
  4500: 'Server engine error. Please retry shortly.',
  4503: 'Server engine unavailable. Please retry shortly.',
};

export class OnlineSocket {
  constructor(roomCode, token, handlers) {
    this.roomCode = roomCode;
    this.token = token;
    this.url = `${WS_BASE}/api/ws/rooms/${roomCode}?token=${encodeURIComponent(token)}`;
    this.handlers = handlers || {};
    this.ws = null;
    this.closed = false;       // caller asked us to close (no auto-reconnect)
    this.joinedOnce = false;   // server sent at least one 'joined' message
    this.openedOnce = false;   // this socket reached OPEN state at least once
    // Reconnect bookkeeping
    this.reconnectStartedAt = 0;
    this.reconnectAttempt = 0;
    this.reconnectTimer = null;
    // PHASE 3.13.2 hotfix: an explicit signal that the client is currently
    // in a "reconnecting" UI state — survives `resume()` calls so a
    // successfully re-attached socket ALWAYS fires onReconnected(),
    // regardless of whether the reconnect was driven by the internal
    // backoff loop or by an external `resume()` (visibilitychange).
    this.reconnectingSignalActive = false;
    // Heartbeat bookkeeping
    this.pingTimer = null;
    this.lastServerMsgAt = 0;
    this.deadCheckTimer = null;
  }

  // -------------------------------------------------------------- connect()
  connect() {
    if (this.closed) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return; // already connecting/connected
    }
    // eslint-disable-next-line no-console
    console.log('[OnlineSocket] connecting', { url: this.url, attempt: this.reconnectAttempt });
    let ws;
    try {
      ws = new WebSocket(this.url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[OnlineSocket] WebSocket constructor threw', e);
      this.handlers.onError?.(`Connection failed: ${String(e && e.message ? e.message : e)}`);
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.openedOnce = true;
      this.lastServerMsgAt = Date.now();
      // eslint-disable-next-line no-console
      console.log('[OnlineSocket] open — awaiting joined', { reconnectAttempt: this.reconnectAttempt });
      this._startHeartbeat();
    };

    ws.onmessage = (e) => {
      this.lastServerMsgAt = Date.now();
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      switch (msg.type) {
        case 'joined': {
          const isReattach = this.joinedOnce; // we'd joined before this socket
          this.joinedOnce = true;
          // eslint-disable-next-line no-console
          console.log('[OnlineSocket] joined', {
            roomCode: msg.roomCode,
            playerId: msg.playerId,
            pod: msg.pod,
            opponentConnected: msg.opponentConnected,
            reattach: isReattach,
            reconnectingSignalActive: this.reconnectingSignalActive,
          });
          // PHASE 3.13.2 hotfix: fire onReconnected whenever a re-attach
          // completes AND the UI was in the "reconnecting" state. The old
          // check `reconnectAttempt > 0` failed when `resume()` had reset
          // the attempt counter before the new socket handshake finished,
          // leaving the reconnect modal stuck on top of a live match.
          if (isReattach && (this.reconnectingSignalActive || this.reconnectAttempt > 0)) {
            // eslint-disable-next-line no-console
            console.log('[OnlineSocket] reconnected', {
              attempts: this.reconnectAttempt,
              signalWasActive: this.reconnectingSignalActive,
            });
            this.reconnectAttempt = 0;
            this.reconnectStartedAt = 0;
            this.reconnectingSignalActive = false;
            this.handlers.onReconnected?.();
          }
          this.handlers.onJoined?.(msg);
          break;
        }
        case 'state':
          this.handlers.onStateMessage?.(msg);
          this.handlers.onState?.(msg.state);
          break;
        case 'events':
          this.handlers.onEvents?.(msg.events || [], msg.tick);
          break;
        case 'error':
          this.handlers.onError?.(msg.message || 'server error');
          break;
        case 'opponent-disconnected':
          this.handlers.onOpponentDisconnected?.();
          break;
        case 'opponent-reconnected':
        case 'opponent-connected':
          // Both message names map to the same UX intent: the partner is
          // live again. `opponent-reconnected` fires mid-match, while
          // `opponent-connected` fires in the waiting room when the
          // creator returns and the match is about to initialise.
          this.handlers.onOpponentReconnected?.();
          break;
        case 'pong':
          break;
        case 'rematch_requested':
        case 'rematch_accepted':
        case 'rematch_declined':
        case 'rematch_expired':
        case 'rematch_started':
        case 'rematch_error':
          this.handlers.onRematchMessage?.(msg);
          break;
        default:
          // eslint-disable-next-line no-console
          console.log('[OnlineSocket] unknown message type', msg.type);
          break;
      }
    };

    ws.onerror = (evt) => {
      // Browsers fire `onerror` for many benign reasons — never escalate
      // straight to UI. The decisive event is `onclose`.
      // eslint-disable-next-line no-console
      console.warn('[OnlineSocket] onerror (debug only):', evt);
    };

    ws.onclose = (evt) => {
      this._stopHeartbeat();
      // eslint-disable-next-line no-console
      console.log('[OnlineSocket] close', {
        code: evt.code,
        reason: evt.reason,
        wasClean: evt.wasClean,
        joinedOnce: this.joinedOnce,
        openedOnce: this.openedOnce,
        closedByCaller: this.closed,
        reconnectAttempt: this.reconnectAttempt,
      });
      if (this.closed) return;

      // 1) Closed BEFORE joining — terminal (preserve old behavior).
      if (!this.joinedOnce) {
        this.handlers.onError?.(CODE_MSGS[evt.code] || `Connection closed before joining (code ${evt.code})`);
        this.handlers.onClose?.();
        return;
      }

      // 2) Server-issued NON_RECOVERABLE code — terminal.
      if (NON_RECOVERABLE_CODES.has(evt.code)) {
        this.handlers.onError?.(CODE_MSGS[evt.code] || `Connection closed (code ${evt.code})`);
        this.handlers.onClose?.();
        return;
      }

      // 3) Mid-match transient drop — schedule a reconnect attempt.
      this._scheduleReconnect(evt);
    };
  }

  // ----------------------------------------------------------- send(action)
  send(action) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.handlers.onError?.('Not connected — please wait for reconnect to complete.');
      return;
    }
    this.ws.send(JSON.stringify({ type: 'action', action }));
  }

  // ---------------------------------------------------- sendRematch(kind, payload?)
  // Phase 3.11 — send a rematch coordination message. `kind` is one of:
  //   'rematch_request' | 'rematch_accept' | 'rematch_decline'
  // Phase 3.11.1 — `payload` may include `previousMatchId` (client's view
  //   of the just-completed match's engineMatchId) so the server can
  //   reject stale actions targeting an older match.
  sendRematch(kind, payload = null) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.handlers.onError?.('Not connected — please wait for reconnect to complete.');
      return;
    }
    const msg = { type: kind };
    if (payload && typeof payload === 'object') Object.assign(msg, payload);
    this.ws.send(JSON.stringify(msg));
  }

  // --------------------------------------------------------------- close()
  close() {
    this.closed = true;
    this._stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try { this.ws?.close(); } catch (e) { /* ignore */ }
  }

  /**
   * Phase 3.8.2 — force a health check + reconnect if needed.
   *
   * iOS Safari (Standalone PWA too) suspends the JS runtime when the app
   * backgrounds, and WebSocket TCP connections are silently killed by the
   * OS after ~30 s. When the user returns, `ws.readyState` may still say
   * OPEN even though the socket is dead. Our heartbeat / dead-check
   * intervals do not fire while suspended, so the reconnect loop never
   * kicks in on its own.
   *
   * Caller should invoke `resume()` from a `visibilitychange` handler when
   * `document.visibilityState === 'visible'` (and also from `pageshow`).
   * The method:
   *   • Resets the reconnect budget so the user gets a fresh 30 s window.
   *   • If `ws.readyState !== OPEN` OR the last server message is stale,
   *     forcibly closes the socket, triggering the normal reconnect path.
   *   • Otherwise sends an immediate ping to sanity-check liveness.
   */
  resume() {
    if (this.closed) return;
    // Grant a fresh reconnect window on every visibility return.
    this.reconnectStartedAt = 0;
    this.reconnectAttempt = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // eslint-disable-next-line no-console
      console.log('[OnlineSocket] resume — no live socket, connecting');
      this.connect();
      return;
    }
    // Socket appears open but iOS may have killed the TCP silently. If we
    // haven't heard from the server in > HEARTBEAT_DEAD_MS, force a close
    // so the normal reconnect path kicks in.
    const staleMs = Date.now() - (this.lastServerMsgAt || Date.now());
    if (staleMs > HEARTBEAT_DEAD_MS) {
      // eslint-disable-next-line no-console
      console.log('[OnlineSocket] resume — server silence > dead threshold, forcing reconnect', { staleMs });
      try { this.ws.close(4099, 'client-resume-forcing-reconnect'); } catch (e) { /* ignore */ }
      return;
    }
    // Otherwise poke the server so we detect a silently-dead socket.
    try {
      this.ws.send(JSON.stringify({ type: 'ping', ts: Date.now(), resume: 1 }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[OnlineSocket] resume ping failed — forcing reconnect', e);
      try { this.ws.close(4099, 'client-resume-ping-failed'); } catch (_e) { /* ignore */ }
    }
  }

  // ============================================== internal: reconnect loop
  _scheduleReconnect(closeEvt) {
    if (this.closed) return;
    if (this.reconnectStartedAt === 0) {
      this.reconnectStartedAt = Date.now();
    }
    const elapsedMs = Date.now() - this.reconnectStartedAt;
    if (elapsedMs >= RECONNECT_BUDGET_MS) {
      // eslint-disable-next-line no-console
      console.log('[OnlineSocket] reconnect budget exhausted', { elapsedMs });
      this.handlers.onError?.('Could not reconnect within 30 s. Please return to the lobby.');
      this.handlers.onClose?.();
      return;
    }
    this.reconnectAttempt += 1;
    // PHASE 3.13.2 hotfix — signal that we're actively in a reconnect
    // cycle. This flag survives `resume()` calls so that once a fresh
    // socket completes its handshake, `onReconnected()` fires and the UI
    // clears its "Reconnecting…" modal.
    this.reconnectingSignalActive = true;
    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * Math.pow(1.5, this.reconnectAttempt - 1),
      RECONNECT_MAX_DELAY_MS,
    );
    // eslint-disable-next-line no-console
    console.log('[OnlineSocket] reconnect attempt', {
      attempt: this.reconnectAttempt,
      delayMs: Math.round(delay),
      elapsedMs,
      totalMs: RECONNECT_BUDGET_MS,
      closeCode: closeEvt.code,
    });
    this.handlers.onReconnecting?.({
      attempt: this.reconnectAttempt,
      elapsedMs,
      totalMs: RECONNECT_BUDGET_MS,
    });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closed) return;
      this.ws = null;
      this.openedOnce = false;
      // NB: keep `joinedOnce = true` so the next `joined` message is
      // recognised as a re-attach and onReconnected() fires.
      this.connect();
    }, delay);
  }

  // ============================================== internal: heartbeat loop
  _startHeartbeat() {
    this._stopHeartbeat();
    this.pingTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      try {
        this.ws.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[OnlineSocket] ping send failed', e);
      }
    }, HEARTBEAT_MS);
    this.deadCheckTimer = setInterval(() => {
      const sinceMs = Date.now() - (this.lastServerMsgAt || Date.now());
      if (sinceMs > HEARTBEAT_DEAD_MS) {
        // eslint-disable-next-line no-console
        console.warn('[OnlineSocket] no server msg in', sinceMs, 'ms — forcing close to trigger reconnect');
        try { this.ws?.close(4099, 'client-detected-dead'); } catch (e) { /* ignore */ }
      }
    }, HEARTBEAT_MS / 2);
  }

  _stopHeartbeat() {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    if (this.deadCheckTimer) { clearInterval(this.deadCheckTimer); this.deadCheckTimer = null; }
  }
}
